<template>
  <div class="app-global-wrapper">
    <Transition name="app-background-fade" mode="out-in">
      <div
        :key="activeBackground.id"
        class="app-background-layer"
        :class="{ 'has-image': Boolean(activeBackground.image) }"
        :style="backgroundLayerStyle"
        aria-hidden="true"
      ></div>
    </Transition>

    <AppNavPanel
      :navItems="desktopNavItems"
      :activeSeasonLabel="activeSeasonLabel"
      :isDarkMode="isDarkMode"
      :updateAvailable="updateAvailable"
      @toggleTheme="toggleTheme"
      @checkUpdates="checkForUpdates"
    />

    <div class="mobile-nav-panel">
      <div class="brand">
        <div class="brand-mark">
            <img src="./assets/images/logo.png">
            <span class="brand-version">v2</span>
        </div>
        <div>
          <p>World of Warcraft</p>
          <h1>Raider.io Dashboard</h1>
        </div>
      </div>
    </div>

    <main class="app-content">
      <div class="page-stack">
        <RouterView />
      </div>

      <footer class="app-footer">
        <div class="item">
                This app use developer API from
            <a href="https://raider.io/" target="_blank" rel="noreferrer">
            Raider.io
            </a>
        </div>
        <div class="item">
            Design and developed by
            <a href="https://andrii-ilutsa.me/" target="_blank" rel="noreferrer">
            Andrii Ilutsa
            </a>
        </div>
        <div class="item app-version-note">
            Version {{ appVersion }}
        </div>
      </footer>
    </main>

    <AppMobileNav :navItems="navItems" />
    <AppNotifications />

    <Transition name="global-loader">
      <div v-if="isGlobalLoading" class="app-global-loader-stack" role="status" aria-live="polite">
        <div v-for="entry in globalLoadingEntries" :key="entry.id" class="app-global-loader">
          <span class="global-loader-spinner" aria-hidden="true"></span>
          <span>{{ entry.message || 'Loading data...' }}</span>
        </div>
      </div>
    </Transition>

    <Transition name="fullscreen-loader">
      <div v-if="isFullScreenLoading" class="app-fullscreen-loader" role="status" aria-live="polite">
        <div class="fullscreen-loader-panel">
          <span class="fullscreen-loader-mark">
            <img src="./assets/images/logo.png" alt="" aria-hidden="true">
          </span>
          <div class="fullscreen-loader-copy">
            <p class="eyebrow">Raider.io Dashboard</p>
            <h2>{{ fullScreenLoadingMessage }}</h2>
            <span>Preparing fresh data and interface assets</span>
          </div>
          <span class="fullscreen-loader-progress" aria-hidden="true"></span>
        </div>
      </div>
    </Transition>

    <Transition name="confirm-modal">
      <div v-if="confirmation" class="confirm-backdrop" role="presentation" @click.self="cancelConfirmation">
        <div class="card confirm-dialog" role="dialog" aria-modal="true" :aria-labelledby="confirmationTitleId">
          <header>
            <p class="eyebrow">Confirm action</p>
            <h2 :id="confirmationTitleId">{{ confirmation.title }}</h2>
          </header>
          <section>
            <p>{{ confirmation.message }}</p>
          </section>
          <footer class="confirm-actions">
            <button class="btn-outline" type="button" @click="cancelConfirmation">
              {{ confirmation.cancelLabel || 'Cancel' }}
            </button>
            <button class="btn-destructive" type="button" @click="confirmAction">
              {{ confirmation.confirmLabel || 'Delete' }}
            </button>
          </footer>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import AppMobileNav from './components/AppMobileNav.vue'
import AppNavPanel from './components/AppNavPanel.vue'
import AppNotifications from './components/AppNotifications.vue'
import { findBackgroundOption } from './constants/backgrounds'

export default {
  name: 'App',
  components: {
    AppMobileNav,
    AppNavPanel,
    AppNotifications
  },
  data() {
    return {
      appVersion: '1.0.0',
    }
  },
  computed: {
    canInstall() {
      return this.$store.getters['getCanInstall']
    },
    updateAvailable() {
      return this.$store.getters['getUpdateAvailable']
    },
    activeSeasonLabel() {
      return this.$store.getters['getActiveSeasonLabel']
    },
    themeMode() {
      return this.$store.getters['getThemeMode']
    },
    appBackground() {
      return this.$store.getters['getAppBackground']
    },
    isDarkMode() {
      return this.themeMode === 'dark'
    },
    activeBackground() {
      return findBackgroundOption(this.appBackground)
    },
    backgroundLayerStyle() {
      if (!this.activeBackground.image) {
        return {}
      }

      const maskStart = this.isDarkMode ? 50 : 36
      const maskEnd = this.isDarkMode ? 70 : 56

      return {
        backgroundImage: `linear-gradient(180deg, color-mix(in oklch, var(--background) ${maskStart}%, transparent), color-mix(in oklch, var(--background) ${maskEnd}%, transparent)), url(${this.activeBackground.image})`,
      }
    },
    confirmation() {
      return this.$store.getters['getConfirmation']
    },
    confirmationTitleId() {
      return 'global-confirmation-title'
    },
    isGlobalLoading() {
      return this.$store.getters['getGlobalLoading']
    },
    globalLoadingMessage() {
      return this.$store.getters['getGlobalLoadingMessage']
    },
    globalLoadingEntries() {
      return this.$store.getters['getGlobalLoadingEntries']
    },
    isFullScreenLoading() {
      return this.$store.getters['getFullScreenLoading']
    },
    fullScreenLoadingMessage() {
      return this.$store.getters['getFullScreenLoadingMessage']
    },
    navItems() {
      return [
        { to: '/mythic-plus', label: 'Mythic+', shortLabel: 'M+', icon: 'fa-solid fa-dungeon', hint: 'Runs, rosters and dungeons' },
        { to: '/raiding', label: 'Raiding', shortLabel: 'Raid', icon: 'fa-solid fa-shield-halved', hint: 'Guild rankings and boss kills' },
        { to: '/news', label: 'News', shortLabel: 'News', icon: 'fa-solid fa-newspaper', hint: 'Wowhead Warcraft feed' },
        { to: '/favorites', label: 'Favorites', shortLabel: 'Fav', icon: 'fa-solid fa-star', hint: 'Saved character profiles' },
        { to: '/gear-insights', label: 'Gear Insights', shortLabel: 'Gear', icon: 'fa-solid fa-shirt', hint: 'Best items by spec' },
        { to: '/compare', label: 'Compare', shortLabel: 'Compare', icon: 'fa-solid fa-scale-balanced', hint: 'Side-by-side character stats' },
        { to: '/about', label: 'About', shortLabel: 'About', icon: 'fa-solid fa-circle-info', hint: 'Features and alternatives' },
        { to: '/settings', label: 'Settings', shortLabel: 'Settings', icon: 'fa-solid fa-gear', hint: 'PWA and local data' },
      ]
    },
    desktopNavItems() {
      return this.navItems.filter((item) => item.to !== '/settings')
    },
  },
  watch: {
    confirmation: {
      immediate: true,
      handler(value) {
        document.body.classList.toggle('is-scroll-locked', Boolean(value))
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.closeConfirmationOnEscape)
    window.addEventListener('beforeunload', this.markReloadLoader)
    this.showReloadLoaderIfNeeded()
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.closeConfirmationOnEscape)
    window.removeEventListener('beforeunload', this.markReloadLoader)
    document.body.classList.remove('is-scroll-locked')
  },
  methods: {
    promptInstall() {
      this.$store.dispatch('promptInstall')
    },
    checkForUpdates() {
      this.$store.dispatch('checkForUpdates')
    },
    applyUpdate() {
      this.$store.dispatch('applyUpdate')
    },
    toggleTheme() {
      this.$store.dispatch('toggleTheme')
    },
    cancelConfirmation() {
      this.$store.dispatch('cancelConfirmation')
    },
    confirmAction() {
      this.$store.dispatch('confirmAction')
    },
    closeConfirmationOnEscape(event) {
      if (event.key === 'Escape' && this.confirmation) {
        this.cancelConfirmation()
      }
    },
    markReloadLoader() {
      try {
        sessionStorage.setItem('rio-dashboard-show-reload-loader', '1')
      } catch (error) {
        console.error('Reload loader flag failed', error)
      }
    },
    showReloadLoaderIfNeeded() {
      try {
        if (sessionStorage.getItem('rio-dashboard-show-reload-loader') !== '1') {
          return
        }
        sessionStorage.removeItem('rio-dashboard-show-reload-loader')
      } catch (error) {
        return
      }

      this.$store.dispatch('showFullScreenLoader', {
        message: 'Refreshing dashboard...',
        duration: 2000,
      })
    },
  },
}
</script>

<style lang="less">
@import './assets/less/index.less';
</style>
